import { useState } from "react";
import { TRANSLATION_LOCALES, type TranslationLocale } from "@mmm/contracts";
import { Button, EmptyState, ErrorState, Input, Select, setCatalogOverrides, SUPPORTED_LANGUAGES, useT } from "@mmm/ui";
import { disableTranslationOverride, setTranslationCampaign } from "../../api/commands.js";
import { getTranslationModeration, getTranslationOverrides } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import { ListRow } from "../molecules/ListRow.js";
import { Modal } from "../shell/Modal.js";

export function TranslationModerationPicker({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [locale, setLocale] = useState<TranslationLocale>("af");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const query = useQuery(getTranslationModeration, { locale });

  async function changeCampaign(enabled: boolean) {
    setBusy(true); setSaved(false);
    try {
      await setTranslationCampaign({ locale, enabled, reason: reason.trim() });
      setSaved(true); setReason("");
    } catch { /* The shared command layer shows the error. */ }
    finally { setBusy(false); }
  }

  async function removeOverride(messageKey: string) {
    setBusy(true); setSaved(false);
    try {
      await disableTranslationOverride({ locale, messageKey, reason: reason.trim() });
      const overlay = await getTranslationOverrides({ locale });
      setCatalogOverrides(locale, overlay.overrides);
      setSaved(true); setReason("");
    } catch { /* The shared command layer shows the error. */ }
    finally { setBusy(false); }
  }

  return <Modal onClose={busy ? undefined : onClose} width={620}>
    <h2>{t("translations.manageCampaign")}</h2>
    <label className="mmm-field-label" htmlFor="moderation-locale">{t("settings.language")}</label>
    <Select id="moderation-locale" value={locale} onChange={(event) => {
      const next = event.target.value;
      if (TRANSLATION_LOCALES.some((code) => code === next)) { setLocale(next as TranslationLocale); setSaved(false); }
    }}>
      {SUPPORTED_LANGUAGES.filter((item) => item.code !== "en").map((item) => <option key={item.code} value={item.code}>{item.nativeName}</option>)}
    </Select>
    <label className="mmm-field-label" htmlFor="moderation-reason">{t("translations.moderationReason")}</label>
    <Input id="moderation-reason" value={reason} minLength={10} maxLength={500} onChange={(event) => setReason(event.target.value)} />
    {saved && <p role="status">{t("translations.moderationSaved")}</p>}
    {!!query.error && <ErrorState variant="list" title={t("errors.serverUnreachable")} onRetry={query.retry} />}
    {query.data && <>
      <p>{t("translations.budgetSummary", { spent: query.data.rewardSpent, reserved: query.data.rewardReserved, budget: query.data.rewardBudget })}</p>
      <Button variant="secondary" disabled={busy || reason.trim().length < 10} onClick={() => void changeCampaign(!query.data?.enabled)}>{t(query.data.enabled ? "translations.pauseCampaign" : "translations.resumeCampaign")}</Button>
      <h3>{t("translations.currentTranslation")}</h3>
      {query.data.activeOverrides.length ? query.data.activeOverrides.map((item) => <ListRow key={item.messageKey} title={item.messageKey} meta={item.value}
        trailing={<Button variant="secondary" disabled={busy || reason.trim().length < 10} onClick={() => void removeOverride(item.messageKey)}>{t("translations.disableOverride")}</Button>} />)
        : <EmptyState variant="list" title={t("translations.noOverrides")} />}
    </>}
    <div className="mmm-modal-actions"><Button variant="secondary" onClick={onClose}>{t("common.close")}</Button></div>
  </Modal>;
}
